import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { Box, Button, FormControl, MenuItem, Divider } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useMemo, useState, Fragment } from 'react';
import {
  Table,
  Pagination,
  Loader,
  ManyFilesUpload,
  ImageModal,
  SMBSelect
} from '../../../../components';
import {
  INeighborhoodAmenity,
  IResponse,
  IDropdownResponse,
  INeighborhoodAmenityDoc
} from '../../../../models';
import {
  getNeighborhoodAmenities,
  getSingleNeighborhoodAmenities,
  createNeighborhoodAmenity,
  createNeighborhoodAmenityImages,
  removeNeighborhoodAmenity
} from '../../../../fetch';
import { ExternalLink } from '../../../../components/link/ExternalLink';

interface INeighborhoodAmenitiesProps {
  neighborhoodId: string | number;
}

export const NeighborhoodAmenitiesPage: FC<INeighborhoodAmenitiesProps> = ({ neighborhoodId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSavingImage, setSavingImage] = useState<{
    [id: number]: boolean;
  }>({});
  const [neighborhoodAmenities, setNeighborhoodAmenities] = useState<INeighborhoodAmenity[]>([]);
  const [currentImage, setCurrentImage] = useState<string>('');
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);

  const { isLoading: isLoadingAllNeighborhoodAmenities, data: allNeighborhoodAmenitiesData } =
    useQuery<IDropdownResponse[], Error>(
      ['allNeighborhoodAmenities'],
      () => getNeighborhoodAmenities(),
      {
        enabled: neighborhoodId !== 'add'
      }
    );

  const {
    isLoading: isLoadingSingleNeighborhoodAmenities,
    refetch: refetchSingleNeighborhoodAmenities
  } = useQuery<IResponse<INeighborhoodAmenity[]>, Error>(
    ['singleNeighborhoodAmenities', neighborhoodId, page, perPage],
    () =>
      getSingleNeighborhoodAmenities(neighborhoodId, {
        page: page + 1,
        perPage
      }),
    {
      enabled: neighborhoodId !== 'add',
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
        setNeighborhoodAmenities(data.records);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleImagesUpload = async (
    files: File[],
    item: INeighborhoodAmenity
  ): Promise<INeighborhoodAmenityDoc[]> => {
    if (files?.length > 0) {
      try {
        setSavingImage({
          [item.amenityId]: true
        });
        const formData = new FormData();
        files.forEach(file => {
          formData.append('files', file);
        });
        const res = await createNeighborhoodAmenityImages(neighborhoodId, item.amenityId, formData);
        enqueueSnackbar('Amenity Updated!', { variant: 'success' });
        return res;
      } catch (error) {
        enqueueSnackbar(error?.Detail, { variant: 'error' });
      } finally {
        setSavingImage({
          [item.amenityId]: false
        });
      }
    }
  };

  const updateMyData = (item: INeighborhoodAmenity, columnId: string, value: any) => {
    setNeighborhoodAmenities(old =>
      old.map((row, index) => {
        if (row.amenityId === item.amenityId) {
          return {
            ...item,
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'amenityType'
      },
      {
        Header: 'Upload Files',
        accessor: 'files',
        Cell: ({ cell: { row } }) => {
          const item: INeighborhoodAmenity = row.original;
          return (
            <Box display="flex" alignItems="center">
              {item.documents?.length > 0 && (
                <>
                  {item.documents.map((doc: INeighborhoodAmenityDoc, index: number) => {
                    return (
                      <Fragment key={`${index}`}>
                        {doc.fileDisplayName.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <picture
                            className={classes.pictureImages}
                            onClick={() => setCurrentImage(doc.downloadUrl)}
                          >
                            <img
                              className={classes.colorImage}
                              src={doc.downloadUrl}
                              alt={doc.fileDisplayName}
                            />
                          </picture>
                        ) : (
                          <ExternalLink to={doc.downloadUrl} target="_blank">
                            File
                          </ExternalLink>
                        )}
                      </Fragment>
                    );
                  })}
                  <Divider flexItem orientation="vertical" sx={{ margin: '0 0.5rem' }} />
                </>
              )}
              <ManyFilesUpload
                isDisabled={isSavingImage[item.amenityId]}
                imageKey={`upload-images-${item.amenityId}`}
                uploadText="Upload"
                accept="image/*"
                handleFileChange={async (files: File[]) => {
                  const res = await handleImagesUpload(files, item);
                  updateMyData(row.original, 'documents', res);
                }}
              />
            </Box>
          );
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INeighborhoodAmenity } };
        }) => {
          return (
            <Button
              startIcon={<Delete />}
              color="error"
              onClick={async () => {
                const result = window.confirm('Are you sure you want to delete this amenity?');
                if (result) {
                  try {
                    setIsDeleting(true);
                    await removeNeighborhoodAmenity(neighborhoodId, original.amenityId);
                    refetchSingleNeighborhoodAmenities();
                    enqueueSnackbar('Amenity Deleted!', {
                      variant: 'success'
                    });
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ?? 'Error deleting amenity, please try again.', {
                      variant: 'error'
                    });
                  } finally {
                    setIsDeleting(false);
                  }
                }
              }}
            >
              Delete
            </Button>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodAmenities, isSavingImage]);
  return (
    <>
      {(isAdding || isDeleting) && <Loader type="overlay" position="centered" />}
      <Box mt={1}>
        <Box display="flex" mb={1}>
          <FormControl
            sx={{
              width: {
                xs: '50%',
                sm: '25rem'
              }
            }}
            variant="outlined"
            size="small"
          >
            <SMBSelect
              SelectProps={{
                multiple: true
              }}
              disabled={isLoadingAllNeighborhoodAmenities}
              name={`amenitiyId`}
              label={isLoadingAllNeighborhoodAmenities ? 'Loading...' : 'Amenity'}
              id={`amenitiyId`}
              variant="outlined"
              handleClear={() => setSelectedAmenities([])}
              value={selectedAmenities}
              size="small"
              onChange={({ target: { value } }) => {
                setSelectedAmenities(typeof value === 'string' ? value.split(',') : value);
              }}
            >
              {!isLoadingAllNeighborhoodAmenities &&
                allNeighborhoodAmenitiesData &&
                allNeighborhoodAmenitiesData.map((amenity, index) => {
                  return (
                    <MenuItem key={`${index}`} value={amenity.value}>
                      {amenity.description}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </FormControl>
          <Button
            startIcon={<Add />}
            color="primary"
            variant="contained"
            sx={{
              width: {
                xs: '50%',
                sm: 'auto'
              }
            }}
            onClick={async () => {
              try {
                setIsAdding(true);
                await createNeighborhoodAmenity(neighborhoodId, { amenities: selectedAmenities });
                enqueueSnackbar(
                  `Success, amenitie${selectedAmenities?.length > 0 ? 's' : ''} added!`,
                  {
                    variant: 'success'
                  }
                );
                refetchSingleNeighborhoodAmenities();
                setSelectedAmenities([]);
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? `Error, please try again.`, {
                  variant: 'error'
                });
              } finally {
                setIsAdding(false);
              }
            }}
            disabled={isAdding || selectedAmenities?.length === 0}
          >
            Add Amenity
          </Button>
        </Box>
        <Table
          columns={columns}
          data={neighborhoodAmenities}
          isLoading={isLoadingSingleNeighborhoodAmenities}
          hidePagination
        />
        {!isLoadingSingleNeighborhoodAmenities && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Box>
      <ImageModal
        isOpen={!!currentImage}
        onClose={() => setCurrentImage('')}
        source={currentImage}
      />
    </>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  pictureImages: {
    marginRight: theme.spacing(1),
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  colorImage: {
    width: 50,
    height: 50
  }
}));
